import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn, formatIDR, formatTimeRange, hexToRgba, isEmpty } from '@/lib/utils';
import { ThemeColorSchemeType } from '@/stores/builder';
import { BasePromotion } from '@/types/http/promotion/common';
import { TicketPercent } from 'lucide-react';

type CardPromotionProps = {
  theme?: ThemeColorSchemeType;
  promo: BasePromotion;
};

export default function CardPromotion({ theme, promo }: CardPromotionProps) {
  const tnc = promo.promotion_tnc;

  const timeRange = tnc?.validSchedule?.time ? formatTimeRange(tnc.validSchedule.time) : null;

  const dayMap: Record<string, string> = {
    Mon: 'Monday',
    Tue: 'Tuesday',
    Wed: 'Wednesday',
    Thu: 'Thursday',
    Fri: 'Friday',
    Sat: 'Saturday',
    Sun: 'Sunday',
  };

  const allDayShort = Object.keys(dayMap);

  const daysString = tnc?.validSchedule?.days || '';
  const daysArrayShort = daysString
    .split(',')
    .map((day) => day.trim())
    .filter((day) => allDayShort.includes(day));

  const daysArrayFull = daysArrayShort.map((d) => dayMap[d]);

  let daysDisplay = '';
  if (daysArrayFull.length === 1) {
    daysDisplay = `Every ${daysArrayFull[0]}`;
  } else if (allDayShort.every((day) => daysArrayShort.includes(day))) {
    daysDisplay = 'Every Day';
  } else {
    daysDisplay = daysArrayFull.join(', ');
  }

  return (
    <div
      className={cn(
        'flex w-full flex-col rounded-lg border-2 border-[--border] bg-[--background] p-4 transition-all duration-150'
      )}
      style={
        {
          '--background': hexToRgba(theme?.background, 0.1),
          '--border': theme?.background,
          '--text-primary': theme?.text,
          '--text-secondary': theme?.textSecondary || '#000',
          '--btn-background': theme?.btnSolidBackgroud,
          '--btn-text': theme?.btnSolidlabel,
        } as React.CSSProperties
      }
    >
      <div className='flex w-full items-center justify-between gap-2'>
        <div
          className={cn(
            'flex w-full items-center gap-3',
            isEmpty(theme) ? 'text-gray-800' : 'text-[var(--text-primary)]'
          )}
        >
          <div className='flex items-center'>
            <TicketPercent className='h-5 w-5' />
          </div>
          <div>
            <h3 className='text-sm font-bold'>{promo.promotion_name}</h3>
            {!(tnc?.typeSpending === 'product' && tnc?.minPurchase === 0) && (
              <span className='text-xs text-[var(--text-secondary)]'>
                Min. Purchase{' '}
                {tnc?.typeSpending === 'amount' || promo.minimum_spending > 1000
                  ? `${formatIDR(promo.minimum_spending)}`
                  : `${promo.minimum_spending} pcs`}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className='relative'>
        <Popover>
          <PopoverTrigger asChild>
            <span
              className={cn(
                'mt-2 cursor-pointer text-xs underline',
                isEmpty(theme) ? 'text-gray-500' : 'text-[var(--text-secondary)]'
              )}
            >
              T&Cs apply
            </span>
          </PopoverTrigger>
          <PopoverContent
            className='z-[9991] max-h-[300px] w-full max-w-sm space-y-2 overflow-y-auto break-words text-xs lg:max-w-lg'
            style={{ color: theme?.textSecondary || '#6B7280' }}
          >
            <div>
              <strong>Promo:</strong> {tnc?.reward}
            </div>
            {tnc?.rules && tnc.rules.length > 0 && (
              <div>
                <strong>Rules:</strong>
                <ul className='list-disc pl-4'>
                  {tnc.rules.map((rule, idx) => (
                    <li key={idx}>{rule}</li>
                  ))}
                </ul>
              </div>
            )}
            {!(tnc?.typeSpending === 'product' && tnc?.minPurchase === 0) && (
              <div>
                <strong>Min {tnc?.typeSpending === 'amount' ? 'Purchase' : 'Qty'}:</strong>{' '}
                {tnc?.typeSpending === 'amount'
                  ? formatIDR(tnc?.minPurchase)
                  : `${tnc?.minPurchase} pcs`}
              </div>
            )}
            <div>
              <strong>Multiplier:</strong> {tnc?.multiplier ? 'Valid' : 'Not Valid'}
            </div>
            {tnc?.rewardSimulation && tnc.rewardSimulation.length > 0 && (
              <div>
                <strong>Reward Simulation:</strong>
                <ul className='list-disc pl-4'>
                  {tnc?.rewardSimulation.map((item, idx) => (
                    <li key={idx}>
                      {item.product_rules?.length > 0 ? (
                        <>
                          {item.product_rules?.length > 0 && (
                            <div className='mb-1'>
                              <span className='font-medium'>Product Rules:</span>{' '}
                              {item.product_rules?.join(', ')}
                            </div>
                          )}
                          <div>
                            <span className='font-medium'>Rewards:</span> {item.rewards.join(' + ')}
                          </div>
                        </>
                      ) : (
                        <>
                          {tnc?.typeSpending === 'amount'
                            ? `Spend ${formatIDR(item.min_spending)}`
                            : `Buy ${item.min_spending} pcs`}{' '}
                          → {Array.isArray(item.rewards) ? item.rewards.join(', ') : ''}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <strong>Valid Schedule:</strong>
              {tnc?.validSchedule?.period && <div>📅 {tnc.validSchedule.period}</div>}
              {tnc?.validSchedule?.applicable_type === 'date' && tnc.validSchedule.date ? (
                <div>
                  📆 Applies on days {tnc.validSchedule.date.start_date}–
                  {tnc.validSchedule.date.end_date} of each month
                </div>
              ) : (
                tnc?.validSchedule.days !== '""' &&
                daysArrayFull.length > 0 && <div>🗓️ {daysDisplay}</div>
              )}
              {timeRange && timeRange !== '00:00 - 00:00' && <div>⏰ {timeRange}</div>}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
